import { isHostedFontVariantCovered } from "@hightouch/lib/brand-profiles/assets/adobe-fonts";
import { sanitizeStyle } from "@hightouch/lib/brand-profiles/assets/font-validation";
import { HOSTED_FONT_LOAD_TIMEOUT_MS } from "src/pages/ad-studio/hooks/hosted-font-constants";

export async function waitForHostedFontFaces(
  name: string,
  variants: readonly { weight: string; style: string }[],
): Promise<void> {
  const declared = Array.from(document.fonts).flatMap((face) => {
    if (
      face.family.replace(/^["']|["']$/g, "").toLowerCase() !==
      name.toLowerCase()
    ) {
      return [];
    }
    const style = sanitizeStyle(face.style);
    return style.rejected ? [] : [{ weight: face.weight, style: style.value }];
  });
  for (const variant of variants) {
    const style = sanitizeStyle(variant.style);
    if (
      style.rejected ||
      !isHostedFontVariantCovered(
        { weight: variant.weight, style: style.value },
        declared,
      )
    ) {
      throw new Error(
        `Font ${name} ${variant.weight} ${variant.style} is missing from the Adobe web project`,
      );
    }
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      Promise.all(
        variants.map(async ({ weight, style }) => {
          const faces = await document.fonts.load(
            `${style} ${weight.split(" ")[0]} 16px "${name}"`,
          );
          if (faces.length === 0) {
            throw new Error(
              `Font ${name} is missing from the Adobe web project`,
            );
          }
        }),
      ),
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(`Font ${name} took too long to load`)),
          HOSTED_FONT_LOAD_TIMEOUT_MS,
        );
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}
